import * as React from 'react';
import cx from 'classnames';
import { ContentState, Modifier, SelectionState } from 'draft-js';
import {
  XmarkIcon,
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon,
  ArrowsUpDownLeftRightIcon,
} from '@revfluence/fresh-icons/regular/esm';
import { Button } from '@/shadcn/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shadcn/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/shadcn/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shadcn/components/ui/dialog';

import { Input } from '@/shadcn/components/ui/input';

import { VideoEntityData } from '../StyleControls/types';

interface IProps {
  contentState: ContentState;
  entityKey: string;
  blockKey: string;
  offsetKey: string;
  updateEntityData?: (newContentState: ContentState, changeType?: string) => void;
}

const ALIGNMENT_OPTIONS = [
  { key: 'left', icon: <AlignLeftIcon width={14} height={14} />, label: 'Left' },
  { key: 'center', icon: <AlignCenterIcon width={14} height={14} />, label: 'Center' },
  { key: 'right', icon: <AlignRightIcon width={14} height={14} />, label: 'Right' },
];

export const VideoBlock: React.FC<IProps> = React.memo((props) => {
  const { contentState, entityKey, updateEntityData } = props;
  const entity = contentState.getEntity(entityKey);
  const data: VideoEntityData = entity.getData();
  const {
    src,
    poster,
    width = 400,
    height = 300,
    alignment = 'left',
    controls = true,
    autoPlay = false,
    muted = false,
    loop = false
  } = data;

  const [isResizing, setIsResizing] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0, width: 0, height: 0 });
  const [resizePreview, setResizePreview] = React.useState({ width: 0, height: 0 });
  const [isPreviewNameModalOpen, setIsPreviewNameModalOpen] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  // Extract filename from URL for default preview name
  const getFileName = React.useCallback((url: string) => {
    try {
      const urlParts = url.split('/');
      const fileNameWithQuery = urlParts[urlParts.length - 1];
      const fileName = fileNameWithQuery.split('?')[0]; // Remove query parameters
      return fileName || 'Video';
    } catch {
      return 'Video';
    }
  }, []);

  const [previewName, setPreviewName] = React.useState(data.previewName || getFileName(src));
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const updateEntity = React.useCallback((newData: Partial<VideoEntityData>) => {
    if (!updateEntityData) return;

    const newContentState = contentState.mergeEntityData(entityKey, newData);

    updateEntityData(newContentState, 'apply-entity');
  }, [contentState, entityKey, updateEntityData]);

  const handleAlignmentChange = React.useCallback((newAlignment: string) => {
    updateEntity({ alignment: newAlignment as 'left' | 'center' | 'right' });
  }, [updateEntity]);

  const handlePreviewNameSave = React.useCallback(() => {
    updateEntity({ previewName });
    setIsPreviewNameModalOpen(false);
  }, [updateEntity, previewName]);

  const handleDelete = React.useCallback(() => {
    if (!updateEntityData) return;

    // Find the block that contains this entity
    const blockKey = props.blockKey;
    const block = contentState.getBlockForKey(blockKey);

    if (!block) return;

    // Create a selection for the entire block
    const blockSelection = SelectionState.createEmpty(blockKey).merge({
      anchorOffset: 0,
      focusOffset: block.getLength(),
    });

    // Remove the entire block containing the video
    const newContentState = Modifier.removeRange(
      contentState,
      blockSelection,
      'backward'
    );

    updateEntityData(newContentState, 'remove-range');
  }, [contentState, updateEntityData, props.blockKey]);

  const handleMouseDown = React.useCallback((event: React.MouseEvent) => {
    if (!videoRef.current) return;

    const rect = videoRef.current.getBoundingClientRect();
    const target = event.target as HTMLElement;

    // Check if the target is the resize handle or any of its children
    const isResizeHandle = target.classList.contains('resize-handle') ||
      target.closest('.resize-handle');

    if (isResizeHandle) {
      event.preventDefault();
      event.stopPropagation();

      const currentWidth = width || rect.width;
      const currentHeight = height || rect.height;

      setIsResizing(true);
      setDragStart({
        x: event.clientX,
        y: event.clientY,
        width: currentWidth,
        height: currentHeight,
      });
      setResizePreview({ width: currentWidth, height: currentHeight });
    }
  }, [width, height]);

  const handleMouseMove = React.useCallback((event: MouseEvent) => {
    if (!isResizing) return;

    const deltaX = event.clientX - dragStart.x;

    // Maintain 16:9 aspect ratio for videos
    const aspectRatio = 16 / 9;
    const newWidth = Math.max(200, Math.min(800, dragStart.width + deltaX));
    const newHeight = Math.max(112, newWidth / aspectRatio);

    setResizePreview({ width: newWidth, height: newHeight });
  }, [isResizing, dragStart]);

  const handleMouseUp = React.useCallback(() => {
    if (isResizing) {
      // Apply the final resize
      const newWidth = Math.round(resizePreview.width);
      const newHeight = Math.round(resizePreview.height);

      updateEntity({
        width: newWidth,
        height: newHeight
      });
      setIsResizing(false);
      setResizePreview({ width: 0, height: 0 });
    }
  }, [isResizing, resizePreview, updateEntity]);

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Get current dimensions for display
  const currentWidth = isResizing ? resizePreview.width : (width || 400);
  const currentHeight = isResizing ? resizePreview.height : (height || 300);

  // Dropdown menus are now inline for better functionality

  return (
    <div
      ref={containerRef}
      className={cx('relative my-2 block', {
        'text-left': alignment === 'left',
        'text-center': alignment === 'center',
        'text-right': alignment === 'right'
      })}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative inline-block max-w-full">
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          width={currentWidth}
          height={currentHeight}
          controls={controls}
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          className="max-w-full h-auto rounded shadow-md transition-shadow duration-200 hover:shadow-lg group-hover:outline group-hover:outline-2 group-hover:outline-blue-400/30 group-hover:outline-offset-2"
          preload="metadata"
        >
          <track kind="captions" label="No captions available" />
        </video>

        {/* Resize overlay during resizing */}
        {isResizing && (
          <div className="absolute inset-0 bg-blue-500/10 border-2 border-dashed border-blue-500 rounded pointer-events-none z-[5]">
            <div className="absolute -top-7 right-0 bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium whitespace-nowrap shadow-lg after:content-[''] after:absolute after:top-full after:right-2 after:border-4 after:border-transparent after:border-t-blue-500">
              {Math.round(currentWidth)} × {Math.round(currentHeight)}
            </div>
          </div>
        )}

        {/* Controls overlay */}
        <div className={cx('absolute top-2 right-2 flex gap-1 transition-all duration-200 z-[100]', {
          'opacity-100 visible': isHovered,
          'opacity-0 invisible': !isHovered
        })}
        >
          {/* Alignment controls */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-8 w-8 p-0 bg-white/90 backdrop-blur border border-gray-200 shadow-sm hover:bg-gray-50 hover:border-blue-400 flex items-center justify-center"
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
              >
                {ALIGNMENT_OPTIONS.find((opt) => opt.key === alignment)?.icon || <AlignLeftIcon width={14} height={14} />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              side="bottom"
              className="z-[9999]"
              sideOffset={4}
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              {ALIGNMENT_OPTIONS.map(({ key, icon, label }) => (
                <DropdownMenuItem
                  key={key}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAlignmentChange(key);
                  }}
                  className={cx({ 'bg-blue-50 text-blue-500': alignment === key })}
                >
                  <div className="flex items-center gap-2">
                    {icon}
                    <span>{label}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Delete button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-8 p-0 bg-white/90 backdrop-blur border border-gray-200 shadow-sm hover:bg-red-50 hover:border-red-400 hover:text-red-600 flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDelete();
                  }}
                >
                  <XmarkIcon width={14} height={14} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete video</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Single resize handle - bottom right corner */}
        <div className={cx('resize-handle absolute -bottom-3 -right-3 w-6 h-6 bg-blue-500 border-2 border-white rounded-full cursor-nw-resize transition-all duration-200 z-10 flex items-center justify-center shadow-md select-none pointer-events-auto hover:bg-blue-400 hover:scale-110 hover:shadow-lg active:bg-blue-600 active:scale-105', {
          'opacity-100 visible': isHovered,
          'opacity-0 invisible': !isHovered
        })}
        >
          <ArrowsUpDownLeftRightIcon width={12} height={12} className="text-white pointer-events-none" />
        </div>
      </div>

      {/* Preview Name Dialog */}
      <Dialog open={isPreviewNameModalOpen} onOpenChange={setIsPreviewNameModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Video Preview Name</DialogTitle>
          </DialogHeader>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Preview Name (this will appear in emails):
            </label>
            <Input
              value={previewName}
              onChange={(e) => setPreviewName(e.target.value)}
              placeholder={`Default: ${getFileName(src)}`}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewNameModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePreviewNameSave}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

VideoBlock.displayName = 'VideoBlock';
